import type React from 'react'
import {useEffect, useMemo, useRef, useState} from 'react'

import {useAppSelector} from '../../../../../../../../hooks/react-redux'
import {getEmptyHash} from '../../../../../../../../utils/empty'
import {usePipelineId} from '../../../../../hooks/pipeline'
import {getValidationErrorMessage} from '../../../../../utils/validators'
import {getDraft} from '../../../../EditPipelinePage.selectors'
import {
  parameterNameValidators,
  parameterValueValidators,
} from '../PipelineParameterForm/usePipelineParameterForm.const'

function usePipelineParameterFormFieldset(
  fieldName: string,
  fieldValue: string,
  parameterNames: string[],
  onChange: (name: string, value: string) => void,
  onValid?: (value: boolean) => void,
) {
  const pipelineId = usePipelineId()
  const isMounted = useRef(false)
  const [parameterNameErrorMessage, setParameterNameErrorMessage] = useState<string | null>()
  const [parameterValueErrorMessage, setParameterValueErrorMessage] = useState<string | null>()
  const deletedParameters = useAppSelector(
    state => state.pipelines.pipelineDraft[pipelineId]?.deleted?.parameters,
  )
  const deletedSecrets = useAppSelector(
    state => state.pipelines.pipelineDraft[pipelineId]?.deleted?.secrets,
  )
  const parameters: Record<string, string | undefined> = useAppSelector(
    state => getDraft(state, pipelineId)?.settings.parameters! ?? getEmptyHash(),
  )
  const secrets: Record<string, string | undefined> = useAppSelector(
    state => getDraft(state, pipelineId)?.settings.secrets ?? getEmptyHash(),
  )
  const compareParamsArray = useMemo(() => {
    const filterSecrets = Object.keys(secrets).filter(secret => !deletedSecrets?.includes(secret))
    const filterParameters = Object.keys(parameters).filter(
      parameter => !deletedParameters?.includes(parameter),
    )
    const commonParameters = [...parameterNames, ...filterParameters]
    const parameterIndex = parameterNames.indexOf(fieldName)

    commonParameters.splice(parameterIndex, 1)

    return [...commonParameters, ...filterSecrets]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secrets, parameters, parameterNames, deletedSecrets, deletedParameters])
  const validateParameterNameHandler = (value: string) => {
    const {message} = getValidationErrorMessage({
      value,
      validators: parameterNameValidators,
      // compareObject: secrets,
      compareArray: compareParamsArray,
    })

    if (message) {
      setParameterNameErrorMessage(message)
    } else {
      setParameterNameErrorMessage(null)
    }
  }
  const validateParameterValueHandler = (value: string) => {
    const {message} = getValidationErrorMessage({
      value,
      validators: parameterValueValidators,
    })

    if (message) {
      setParameterValueErrorMessage(message)
    } else {
      setParameterValueErrorMessage(null)
    }
  }
  const parameterNameChangeInputHandler = ({target}: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = target

    validateParameterNameHandler(value)
    onChange(value, fieldValue)
  }
  const parameterValueChangeInputHandler = ({target}: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = target

    validateParameterValueHandler(value)
    onChange(fieldName, value)
  }
  const isFormInvalid = Boolean(
    parameterNameErrorMessage || parameterValueErrorMessage || !fieldName || !fieldValue,
  )

  useEffect(() => {
    if (isMounted.current) {
      validateParameterNameHandler(fieldName)
    }
    isMounted.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareParamsArray])

  useEffect(() => {
    onValid?.(!isFormInvalid)
  }, [onValid, isFormInvalid])

  return {
    parameterNameErrorMessage,
    parameterValueErrorMessage,
    parameterNameChangeInputHandler,
    parameterValueChangeInputHandler,
  }
}

export default usePipelineParameterFormFieldset
